import { useCallback, useContext, useMemo } from 'react'
import { CustomerFacingDisplayContext } from './CustomerFacingDisplayProvider'
import {
    CFDClientMessageType,
    FinishPaymentMessageType,
    OrderMessageType,
    PaymentMessageType,
} from '../reducer/Interface'
import { CFDClientMessage } from 'hooks/useHardwareAgent/CFD/types'
import { get } from 'lodash-es'
import nameof from 'utilities/nameof'
import cfdToast from 'views/CustomerFacingDisplay/utils/cfdToast'
const MAKE_PAYMENT_MESSAGE_FIELD = nameof<PaymentMessageType>()
const FINISH_PAYMENT_MESSAGE_FIELD = nameof<FinishPaymentMessageType>()
const ORDER_MESSAGE_FIELD = nameof<OrderMessageType>()
export const useCFDContext = () => {
    const {
        handleConnectWithPOS,
        handleSendMessageToPOS,
        CFDClientHub,
        clientID,
        hwAgentUrl,
        payload,
        dispatch,
        clearHWAgent,
        ...other
    } = useContext(CustomerFacingDisplayContext)
    const loadedLocalSetting = useCallback(
        (payload: {
            hwAgentUrl: string | undefined
            clientID: string | undefined
        }) => {
            dispatch({
                type: 'CFD_LOADED_SETTING',
                payload,
            })
        },
        [dispatch],
    )
    const setHWAgentURL = useCallback(
        (url: string | undefined, isAuto?: boolean) => {
            dispatch({
                type: 'CFD_SET_HW_URI',
                payload: { url, isAuto },
            })
            clearHWAgent()
        },
        [clearHWAgent, dispatch],
    )

    const setClientID = useCallback(
        (clientID: string | undefined) => {
            dispatch({
                type: 'CFD_SET_CLIENT',
                payload: { clientID },
            })
        },
        [dispatch],
    )
    const onDisconnectedToPOS = useCallback(() => {
        dispatch({
            type: 'CFD_DISCONNTECTED_TO_POS',
        })
    }, [dispatch])

    const onConnectToPOS = useCallback(async () => {
        if (!(CFDClientHub && clientID)) {
            return
        }
        await handleConnectWithPOS(clientID, onDisconnectedToPOS)
        await handleSendMessageToPOS(
            {
                type: CFDClientMessageType.Connected,
            },
            clientID,
        )

        return
    }, [
        CFDClientHub,
        clientID,
        handleConnectWithPOS,
        handleSendMessageToPOS,
        onDisconnectedToPOS,
    ])
    const onReceiveConnected = useCallback(
        (message: CFDClientMessage) => {
            if (message.type === CFDClientMessageType.Connected) {
                setClientID(message.posClientID)
                cfdToast.success('Connected')
            }
        },
        [setClientID],
    )
    const onReceivePOSMessage = useCallback(
        (message: CFDClientMessage) => {
            dispatch({
                type: 'CFD_CONNTECTED_TO_POS',
            })
            if (message.type !== CFDClientMessageType.Connected) {
                dispatch({
                    type: 'CFD_SET_PAYLOAD',
                    payload: message,
                })
            }
        },
        [dispatch],
    )

    const paymentPayload = useMemo(() => {
        if (
            payload?.type === CFDClientMessageType.Payment &&
            payload?.payload
        ) {
            try {
                const object = JSON.parse(payload?.payload!)
                if (
                    get(
                        object,
                        MAKE_PAYMENT_MESSAGE_FIELD.paymentTypeID,
                        undefined,
                    ) !== undefined
                )
                    return object as PaymentMessageType
            } catch {
                console.error(
                    'unable parse the CFDClientMessageType.Payment: ',
                    payload.payload,
                )
                return undefined
            }
        }
        return undefined
    }, [payload?.payload, payload?.type])

    const finishPaymentPayload = useMemo(() => {
        if (
            payload?.type === CFDClientMessageType.FinishPayment &&
            payload?.payload
        ) {
            try {
                const object = JSON.parse(payload?.payload!)
                if (
                    get(
                        object,
                        FINISH_PAYMENT_MESSAGE_FIELD.isSuccessful,
                        undefined,
                    ) !== undefined
                )
                    return object as FinishPaymentMessageType
            } catch {
                console.error(
                    'unable parse the CFDClientMessageType.FinishPayment: ',
                    payload.payload,
                )
                return undefined
            }
        }
        return undefined
    }, [payload?.payload, payload?.type])

    const orderPayload = useMemo(() => {
        if (payload?.type === CFDClientMessageType.Order && payload?.payload) {
            try {
                const object = JSON.parse(payload?.payload!)
                if (
                    get(object, ORDER_MESSAGE_FIELD.orderItems, undefined) !==
                    undefined
                )
                    return object as OrderMessageType
            } catch {
                console.error(
                    'unable parse the CFDClientMessageType.Order: ',
                    payload.payload,
                )
                return undefined
            }
        }
        return undefined
    }, [payload?.payload, payload?.type])

    return {
        ...other,
        clientID,
        CFDClientHub,
        onConnectToPOS,
        onDisconnectedToPOS,
        handleSendMessageToPOS,
        onReceiveConnected,
        setClientID,
        onReceivePOSMessage,
        orderPayload,
        finishPaymentPayload,
        paymentPayload,
        setHWAgentURL,
        hwAgentUrl,
        loadedLocalSetting,
    }
}
